"use client";

import { c as _c } from "react/compiler-runtime";
import { Baby, Utensils, Droplets, Clock, PencilIcon, Bed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import useLocalStorage from "@/hooks/use-local-storage";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useEffect, useState } from "react";
import { formatDate, formatDateTimeLocal, formatRelativeDate, formatDateDifference } from "@/lib/date";
import BabyTrackerGraph from "./graph";
import { formatTime } from "@/features/tools/lib/time";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ToolData } from "..";
import { Label } from "@/components/ui/label";
type ActivityBase = {
  id: string;
  notes?: string;
};
type FeedingActivity = ActivityBase & {
  type: "feeding";
  time: Date;
};
type PoopingActivity = ActivityBase & {
  type: "pooping";
  time: Date;
};
type WeeActivity = ActivityBase & {
  type: "wee";
  time: Date;
};
type SleepingActivity = ActivityBase & {
  type: "sleeping";
  time: Date;
  startTime: Date;
  endTime: Date;
};
type Activity = FeedingActivity | PoopingActivity | WeeActivity | SleepingActivity;
const STORAGE_KEY = "baby-tracker-activities";
function BabyTracker(t0) {
  const $ = _c(108);
  const {
    title,
    description
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [activities, setActivities] = useLocalStorage(STORAGE_KEY, t1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activityType, setActivityType] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [prevActivity, setPreviousActivity] = useState(null);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = formatDate(new Date());
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [selectedDateTime, setSelectedDateTime] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = formatDateTimeLocal(new Date());
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [selectedStartTime, setSelectedStartTime] = useState(t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = formatDateTimeLocal(new Date());
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [selectedEndTime, setSelectedEndTime] = useState(t4);
  const [selectedNotes, setSelectedNotes] = useState("");
  let t5;
  if ($[4] !== activities) {
    t5 = () => {
      const fortyEightHoursAgo = new Date();
      fortyEightHoursAgo.setHours(fortyEightHoursAgo.getHours() - 48);
      return activities.filter(activity => new Date(activity.time) >= fortyEightHoursAgo);
    };
    $[4] = activities;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const getRecentActivities = t5;
  const recentActivities = getRecentActivities();
  if (!editingActivity && prevActivity) {
    setPreviousActivity(null);
    setSelectedDateTime(formatDateTimeLocal(new Date()));
    setSelectedNotes("");
    setActivityType(null);
  }
  if (editingActivity && editingActivity.id !== prevActivity?.id) {
    setPreviousActivity(editingActivity);
    if (editingActivity.type === "sleeping") {
      setSelectedStartTime(formatDateTimeLocal(new Date(editingActivity.startTime)));
      setSelectedEndTime(formatDateTimeLocal(new Date(editingActivity.endTime)));
    } else {
      setSelectedDateTime(formatDateTimeLocal(new Date(editingActivity.time)));
    }
    setSelectedNotes(editingActivity.notes || "");
    setActivityType(editingActivity.type);
  }
  let t6;
  if ($[6] !== editingActivity || $[7] !== selectedDateTime || $[8] !== selectedEndTime || $[9] !== selectedNotes || $[10] !== selectedStartTime || $[11] !== setActivities) {
    t6 = type => {
      if (editingActivity) {
        setActivities(prev => prev.map(activity_0 => {
          if (activity_0.id !== editingActivity.id) {
            return activity_0;
          }
          if (activity_0.type === "sleeping") {
            return {
              ...activity_0,
              startTime: new Date(selectedStartTime),
              endTime: new Date(selectedEndTime),
              time: new Date(selectedStartTime),
              notes: selectedNotes
            };
          }
          return {
            ...activity_0,
            time: new Date(selectedDateTime),
            notes: selectedNotes
          };
        }));
      } else {
        let newActivity;
        bb42: switch (type) {
          case "feeding":
          case "pooping":
          case "wee":
            {
              newActivity = {
                id: Date.now().toString(),
                type,
                time: new Date(selectedDateTime),
                notes: selectedNotes
              };
              break bb42;
            }
          case "sleeping":
            {
              newActivity = {
                id: Date.now().toString(),
                type: "sleeping",
                time: new Date(selectedStartTime),
                startTime: new Date(selectedStartTime),
                endTime: new Date(selectedEndTime),
                notes: selectedNotes
              };
            }
        }
        setActivities(prev_0 => {
          const updatedActivities = [newActivity, ...prev_0];
          return updatedActivities.sort(_temp);
        });
      }
      setIsDialogOpen(false);
      setActivityType(null);
      setEditingActivity(null);
    };
    $[6] = editingActivity;
    $[7] = selectedDateTime;
    $[8] = selectedEndTime;
    $[9] = selectedNotes;
    $[10] = selectedStartTime;
    $[11] = setActivities;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const saveActivity = t6;
  let t7;
  if ($[13] !== setActivities) {
    t7 = id => {
      setActivities(prev_1 => prev_1.filter(activity_1 => activity_1.id !== id));
    };
    $[13] = setActivities;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const deleteActivity = t7;
  const getActivityIcon = _temp2;
  const getActivityVariant = _temp3;
  let t8;
  if ($[15] !== activities) {
    t8 = type_2 => {
      const lastActivity = activities.find(activity_2 => activity_2.type === type_2);
      return lastActivity ? formatRelativeDate(lastActivity.time) : "Never";
    };
    $[15] = activities;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const getLastActivity = t8;
  let t10;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const onFocus = () => {
        setSelectedDateTime(formatDate(new Date()));
        setSelectedStartTime(formatDateTimeLocal(new Date()));
        setSelectedEndTime(formatDateTimeLocal(new Date()));
      };
      window.addEventListener("focus", onFocus);
      return () => {
        window.removeEventListener("focus", onFocus);
      };
    };
    t10 = [];
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  useEffect(t9, t10);
  const T0 = Dialog;
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = change => {
      setIsDialogOpen(change);
      if (!change) {
        setEditingActivity(null);
        setSelectedNotes("");
      }
    };
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== activityType || $[21] !== saveActivity) {
    t12 = e => {
      e.preventDefault();
      if (activityType) {
        saveActivity(activityType);
      }
    };
    $[20] = activityType;
    $[21] = saveActivity;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = editingActivity ? "Edit" : "Add";
  let t14;
  if ($[23] !== activityType || $[24] !== t13) {
    t14 = <DialogHeader><DialogTitle>{t13} {activityType}</DialogTitle></DialogHeader>;
    $[23] = activityType;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== activityType || $[27] !== selectedDateTime || $[28] !== selectedEndTime || $[29] !== selectedStartTime) {
    t15 = activityType === "sleeping" ? <><div className="grid grid-cols-4 items-center gap-4"><Label htmlFor="starttime" className="text-right">Start Time</Label><Input id="starttime" type="datetime-local" value={selectedStartTime} onChange={e_0 => {
          setSelectedStartTime(e_0.target.value);
          if (new Date(e_0.target.value) > new Date(selectedEndTime)) {
            setSelectedEndTime(e_0.target.value);
          }
        }} className="col-span-3" /></div><div className="grid grid-cols-4 items-center gap-4"><Label htmlFor="endtime" className="text-right">End Time</Label><Input id="endtime" type="datetime-local" value={selectedEndTime} onChange={e_1 => setSelectedEndTime(e_1.target.value)} className="col-span-3" /></div></> : <div className="grid grid-cols-4 items-center gap-4"><Label htmlFor="datetime" className="text-right">{"Date & Time"}</Label><Input id="datetime" type="datetime-local" value={selectedDateTime} onChange={e_2 => setSelectedDateTime(e_2.target.value)} className="col-span-3" /></div>;
    $[26] = activityType;
    $[27] = selectedDateTime;
    $[28] = selectedEndTime;
    $[29] = selectedStartTime;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Label htmlFor="notes" className="text-right">Notes</Label>;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = e_3 => setSelectedNotes(e_3.target.value);
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== selectedNotes) {
    t18 = <div className="grid grid-cols-4 items-center gap-4">{t16}<Textarea id="notes" placeholder="Add any notes..." value={selectedNotes} onChange={t17} className="col-span-3" /></div>;
    $[33] = selectedNotes;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t15 || $[36] !== t18) {
    t19 = <div className="grid gap-4 py-4">{t15}{t18}</div>;
    $[35] = t15;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== deleteActivity || $[39] !== editingActivity) {
    t20 = editingActivity && <Button variant="destructive" onClick={() => {
      deleteActivity(editingActivity.id);
      setIsDialogOpen(false);
      setEditingActivity(null);
    }}>Delete</Button>;
    $[38] = deleteActivity;
    $[39] = editingActivity;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Button type="submit">Save changes</Button>;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t20) {
    t22 = <DialogFooter>{t20}{t21}</DialogFooter>;
    $[42] = t20;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== t12 || $[45] !== t14 || $[46] !== t19 || $[47] !== t22) {
    t23 = <DialogContent className="sm:max-w-[425px]"><form onSubmit={t12}>{t14}{t19}{t22}</form></DialogContent>;
    $[44] = t12;
    $[45] = t14;
    $[46] = t19;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  const t24 = "flex flex-col gap-6";
  let t25;
  if ($[49] !== title) {
    t25 = <h1 className="text-foreground mb-2 text-3xl font-bold">{title}</h1>;
    $[49] = title;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== description) {
    t26 = <p className="text-muted-foreground">{description}</p>;
    $[51] = description;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== t25 || $[54] !== t26) {
    t27 = <div className="text-center">{t25}{t26}</div>;
    $[53] = t25;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => {
      setActivityType("feeding");
      setIsDialogOpen(true);
    };
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  let t29;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Utensils className="mr-2 h-6 w-6" />;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== getLastActivity) {
    t30 = getLastActivity("feeding");
    $[58] = getLastActivity;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== t30) {
    t31 = <DialogTrigger render={<Button onClick={t28} size="lg" variant="default">{t29}Log Feeding<span className="ml-auto text-sm opacity-75">Last: {t30}</span></Button>} />;
    $[60] = t30;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  let t32;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => {
      setActivityType("pooping");
      setIsDialogOpen(true);
    };
    $[62] = t32;
  } else {
    t32 = $[62];
  }
  let t33;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Baby className="mr-2 h-6 w-6" />;
    $[63] = t33;
  } else {
    t33 = $[63];
  }
  let t34;
  if ($[64] !== getLastActivity) {
    t34 = getLastActivity("pooping");
    $[64] = getLastActivity;
    $[65] = t34;
  } else {
    t34 = $[65];
  }
  let t35;
  if ($[66] !== t34) {
    t35 = <DialogTrigger render={<Button onClick={t32} size="lg" variant="secondary">{t33}Log Pooping<span className="ml-auto text-sm opacity-75">Last: {t34}</span></Button>} />;
    $[66] = t34;
    $[67] = t35;
  } else {
    t35 = $[67];
  }
  let t36;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = () => {
      setActivityType("wee");
      setIsDialogOpen(true);
    };
    $[68] = t36;
  } else {
    t36 = $[68];
  }
  let t37;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <Droplets className="mr-2 h-6 w-6" />;
    $[69] = t37;
  } else {
    t37 = $[69];
  }
  let t38;
  if ($[70] !== getLastActivity) {
    t38 = getLastActivity("wee");
    $[70] = getLastActivity;
    $[71] = t38;
  } else {
    t38 = $[71];
  }
  let t39;
  if ($[72] !== t38) {
    t39 = <DialogTrigger render={<Button onClick={t36} size="lg" variant="outline">{t37}Log Wee<span className="ml-auto text-sm opacity-75">Last: {t38}</span></Button>} />;
    $[72] = t38;
    $[73] = t39;
  } else {
    t39 = $[73];
  }
  let t40;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = () => {
      setActivityType("sleeping");
      setIsDialogOpen(true);
    };
    $[74] = t40;
  } else {
    t40 = $[74];
  }
  let t41;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <Bed className="mr-2 h-6 w-6" />;
    $[75] = t41;
  } else {
    t41 = $[75];
  }
  let t42;
  if ($[76] !== getLastActivity) {
    t42 = getLastActivity("sleeping");
    $[76] = getLastActivity;
    $[77] = t42;
  } else {
    t42 = $[77];
  }
  let t43;
  if ($[78] !== t42) {
    t43 = <DialogTrigger render={<Button onClick={t40} size="lg" variant="destructive">{t41}Log Sleep<span className="ml-auto text-sm opacity-75">Last: {t42}</span></Button>} />;
    $[78] = t42;
    $[79] = t43;
  } else {
    t43 = $[79];
  }
  let t44;
  if ($[80] !== t31 || $[81] !== t35 || $[82] !== t39 || $[83] !== t43) {
    t44 = <div className="flex flex-col gap-3">{t31}{t35}{t39}{t43}</div>;
    $[80] = t31;
    $[81] = t35;
    $[82] = t39;
    $[83] = t43;
    $[84] = t44;
  } else {
    t44 = $[84];
  }
  const T1 = Card;
  let t45;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <CardTitle className="flex items-center gap-2"><Clock className="h-5 w-5" />Recent Activities</CardTitle>;
    $[85] = t45;
  } else {
    t45 = $[85];
  }
  const t46 = recentActivities.length === 0 ? "No activities logged in the last 48 hours" : `${recentActivities.length} activities logged in the last 48 hours`;
  let t47;
  if ($[86] !== t46) {
    t47 = <CardHeader>{t45}<CardDescription>{t46}</CardDescription></CardHeader>;
    $[86] = t46;
    $[87] = t47;
  } else {
    t47 = $[87];
  }
  const t48 = recentActivities.length === 0 ? <div className="text-muted-foreground py-8 text-center"><Baby className="mx-auto mb-3 h-12 w-12 opacity-50" /><p>No recent activities to display.</p></div> : recentActivities.map(activity_3 => <div key={activity_3.id} className="active:bg-accent hover:bg-accent flex cursor-pointer flex-row items-center justify-between gap-3 px-6 py-4" onClick={() => {
    setEditingActivity(activity_3);
    setIsDialogOpen(true);
  }}><div className="flex w-24"><Badge variant={getActivityVariant(activity_3.type)} className="flex-1 justify-center">{getActivityIcon(activity_3.type)}<span className="ml-1 capitalize">{activity_3.type}</span></Badge></div><div className="text-muted-foreground flex-1 text-sm">{activity_3.type === "sleeping" ? <><div className="font-medium">{formatTime(activity_3.startTime)}{" "}- {formatTime(activity_3.endTime)}</div><div>{formatDateDifference(new Date(activity_3.startTime), new Date(activity_3.endTime))}</div></> : <><div className="font-medium">{formatDate(activity_3.time)}</div><div>{formatTime(activity_3.time)} •{" "}{formatRelativeDate(activity_3.time)}</div></>}{activity_3.notes && <div className="text-muted-foreground text-xs">{activity_3.notes}</div>}</div><Button disabled={true} variant="ghost" size="sm"><PencilIcon className="h-4 w-4" /></Button></div>);
  let t49;
  if ($[88] !== T1 || $[89] !== t47 || $[90] !== t48) {
    t49 = <T1>{t47}{t48}</T1>;
    $[88] = T1;
    $[89] = t47;
    $[90] = t48;
    $[91] = t49;
  } else {
    t49 = $[91];
  }
  let t50;
  if ($[92] !== activities) {
    t50 = activities.length > 0 && <Card><CardHeader><CardTitle>Today's Summary</CardTitle></CardHeader><CardContent><div className="grid grid-cols-4 gap-4 pt-4 text-center">{(["feeding", "pooping", "wee", "sleeping"] as const).map(type_3 => {
            const todayCount = activities.filter(activity_4 => activity_4.type === type_3 && new Date(activity_4.time).toDateString() === new Date().toDateString());
            if (type_3 === "sleeping") {
              const totalSleep = todayCount.reduce(_temp4, 0);
              const hours = Math.floor(totalSleep / 3600000);
              const minutes = Math.floor(totalSleep % 3600000 / 60000);
              return <div key={type_3} className="space-y-1"><div className="text-foreground text-xl font-bold">{hours}h {minutes}m</div><div className="text-muted-foreground text-sm capitalize">Sleep</div></div>;
            }
            return <div key={type_3} className="space-y-1"><div className="text-foreground text-xl font-bold">{todayCount.length}</div><div className="text-muted-foreground text-sm capitalize">{type_3}{todayCount.length !== 1 ? "s" : ""}</div></div>;
          })}</div></CardContent></Card>;
    $[92] = activities;
    $[93] = t50;
  } else {
    t50 = $[93];
  }
  let t51;
  if ($[94] !== activities) {
    t51 = <BabyTrackerGraph activities={activities} />;
    $[94] = activities;
    $[95] = t51;
  } else {
    t51 = $[95];
  }
  let t52;
  if ($[96] !== t27 || $[97] !== t44 || $[98] !== t49 || $[99] !== t50 || $[100] !== t51) {
    t52 = <div className={t24}>{t27}{t44}{t49}{t50}{t51}</div>;
    $[96] = t27;
    $[97] = t44;
    $[98] = t49;
    $[99] = t50;
    $[100] = t51;
    $[101] = t52;
  } else {
    t52 = $[101];
  }
  let t53;
  if ($[102] !== T0 || $[103] !== isDialogOpen || $[104] !== t11 || $[105] !== t23 || $[106] !== t52) {
    t53 = <T0 open={isDialogOpen} onOpenChange={t11}>{t23}{t52}</T0>;
    $[102] = T0;
    $[103] = isDialogOpen;
    $[104] = t11;
    $[105] = t23;
    $[106] = t52;
    $[107] = t53;
  } else {
    t53 = $[107];
  }
  return t53;
}
function _temp4(acc, activity_5) {
  if (activity_5.type === "sleeping") {
    return acc + (new Date(activity_5.endTime).getTime() - new Date(activity_5.startTime).getTime());
  }
  return acc;
}
function _temp3(type_1) {
  switch (type_1) {
    case "feeding":
      {
        return "default";
      }
    case "pooping":
      {
        return "secondary";
      }
    case "wee":
      {
        return "outline";
      }
    case "sleeping":
      {
        return "destructive";
      }
  }
}
function _temp2(type_0) {
  switch (type_0) {
    case "feeding":
      {
        return <Utensils className="h-4 w-4" />;
      }
    case "pooping":
      {
        return <Baby className="h-4 w-4" />;
      }
    case "wee":
      {
        return <Droplets className="h-4 w-4" />;
      }
    case "sleeping":
      {
        return <Bed className="h-4 w-4" />;
      }
  }
}
function _temp(a, b) {
  return new Date(b.time).getTime() - new Date(a.time).getTime();
}
export { BabyTracker };